import type { IconType } from 'react-icons';
import {
  FaTimesCircle,
  FaPhone,
  FaTag,
  FaUtensils,
  FaCheckCircle,
  FaSuitcaseRolling,
  FaPencilAlt,
  FaBook,
} from 'react-icons/fa';

export interface CalendarEventTypeConfig {
  id: string; // タイプ識別子
  label: string; // 表示用標準ラベル
  icon: IconType; // 表示用 React Icon
  iconColor: string; // カレンダーGrid内のアイコン色
  iconBg: string; // アイコン背景色
  cellBg: string; // 未選択時のカレンダーマス背景色
  selectedBg: string; // 選択時のカレンダーマス背景色
  badgeBg: string; // 詳細パネルのアイコン背景色
  badgeTextColor: string; // 詳細パネルのテキスト色
  isClosedDay?: boolean; // 定休日・休業日（グレーアウト）扱いにするか
  defaultTime?: string; // 通常営業等のデフォルト時間表記
  keywords: string[]; // サマリー文字列からの自動判定キーワード
}

// イベントタイプの一括定義データ（新タイプ追加時はここに追加するだけで全機能に反映されます）
export const EVENT_TYPE_CONFIGS: Record<string, CalendarEventTypeConfig> = {
  closed: {
    id: 'closed',
    label: '定休日',
    icon: FaTimesCircle,
    iconColor: 'text-gray-500',
    iconBg: 'bg-gray-200/60',
    cellBg: 'bg-gray-100/90 hover:bg-gray-200/70 opacity-60',
    selectedBg: 'bg-gray-500 text-white hover:bg-gray-600',
    badgeBg: 'bg-accent/10',
    badgeTextColor: 'text-accent',
    isClosedDay: true,
    keywords: ['定休', '休業', '休み', '休日'],
  },
  reservations: {
    id: 'reservations',
    label: '予約',
    icon: FaPhone,
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-100',
    cellBg: 'bg-amber-50/60 hover:bg-amber-100/60',
    selectedBg: 'bg-amber-500 text-white hover:bg-amber-600',
    badgeBg: 'bg-amber-500/10',
    badgeTextColor: 'text-amber-600',
    keywords: ['予約'],
  },
  private: {
    id: 'private',
    label: '貸切',
    icon: FaTag,
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-100',
    cellBg: 'bg-amber-50/60 hover:bg-amber-100/60',
    selectedBg: 'bg-amber-500 text-white hover:bg-amber-600',
    badgeBg: 'bg-amber-500/10',
    badgeTextColor: 'text-amber-600',
    keywords: ['貸切', '貸し切り'],
  },
  full: {
    id: 'full',
    label: '満室',
    icon: FaSuitcaseRolling,
    iconColor: 'text-stone-500',
    iconBg: 'bg-stone-100',
    cellBg: 'bg-stone-50/60 hover:bg-stone-100/60',
    selectedBg: 'bg-stone-500 text-white hover:bg-stone-600',
    badgeBg: 'bg-stone-500/10',
    badgeTextColor: 'text-stone-600',
    keywords: ['満室'],
  },
  event: {
    id: 'event',
    label: 'イベント',
    icon: FaUtensils,
    iconColor: 'text-emerald-500',
    iconBg: 'bg-emerald-100',
    cellBg: 'bg-emerald-50/60 hover:bg-emerald-100/60',
    selectedBg: 'bg-emerald-600 text-white hover:bg-emerald-700',
    badgeBg: 'bg-emerald-500/10',
    badgeTextColor: 'text-emerald-600',
    keywords: [],
  },
  food: {
    id: 'food',
    label: '食の会',
    icon: FaPencilAlt,
    iconColor: 'text-orange-500',
    iconBg: 'bg-orange-100',
    cellBg: 'bg-orange-50/60 hover:bg-orange-100/60',
    selectedBg: 'bg-orange-500 text-white hover:bg-orange-600',
    badgeBg: 'bg-orange-500/10',
    badgeTextColor: 'text-orange-600',
    keywords: ['食の会'],
  },
  reading: {
    id: 'reading',
    label: '読書会',
    icon: FaBook,
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-100',
    cellBg: 'bg-blue-50/60 hover:bg-blue-100/60',
    selectedBg: 'bg-blue-500 text-white hover:bg-blue-600',
    badgeBg: 'bg-blue-500/10',
    badgeTextColor: 'text-blue-600',
    keywords: ['読書会'],
  },
  open: {
    id: 'open',
    label: '通常営業',
    icon: FaCheckCircle,
    iconColor: 'text-accent2',
    iconBg: 'bg-accent2/10',
    cellBg: 'bg-transparent hover:bg-natural-light/40',
    selectedBg: 'bg-accent text-natural-light hover:bg-accent/90',
    badgeBg: 'bg-accent2/10',
    badgeTextColor: 'text-accent2',
    defaultTime: '11:00〜15:00 (夜は予約制)',
    keywords: [],
  },
};

// サマリー文字列や extendedProperties から config を判定するヘルパー関数
export const resolveEventConfig = (
  typeKey?: string,
  summary: string = '',
): CalendarEventTypeConfig => {
  if (typeKey && EVENT_TYPE_CONFIGS[typeKey]) {
    return EVENT_TYPE_CONFIGS[typeKey];
  }

  // サマリー文字列のキーワード判定
  if (summary) {
    for (const config of Object.values(EVENT_TYPE_CONFIGS)) {
      if (config.keywords.some((kw) => summary.includes(kw))) {
        return config;
      }
    }
    // データはあるがどのキーワードにも該当しない場合は event (イベント)
    return EVENT_TYPE_CONFIGS.event;
  }

  if (typeKey) {
    return EVENT_TYPE_CONFIGS.event;
  }

  // 何もデータがない場合は open (通常営業)
  return EVENT_TYPE_CONFIGS.open;
};
